/**
 * Effects and videos of extra.json (W1-10a; formats-and-packaging P§5, DESIGN.md §6.1). The authoring UI is P1;
 * these commands serve merge, the Raw panel, the asset commands and checker fixes.
 *
 * Rules: an effect is active on [start, end) with end > start; a custom shader is an existing asset; variable
 * names are GLSL identifiers, unique per effect; a constant matches the variable's type (float, vec2, RGBA colour of
 * u8 channels); a keyframed variable points at a track the effect (or video) owns, and replacing or removing it
 * deletes that track. The effect order (post-processing order) is a permutation of the effects.
 */

import { isTrackValueOf, varValueType } from '../model/invariants';
import type {
  AssetRef,
  BuiltinShader,
  Effect,
  EffectId,
  EffectVar,
  Video,
  VideoId,
  VideoVarValue,
} from '../model/types';
import { Beats } from '../time/beat';
import type { Beat } from '../time/types';
import {
  assetOf,
  deleteTrack,
  effectOf,
  isFiniteNumber,
  lineOf,
  reorderedSubset,
  videoOf,
  type Patch,
  type Tx,
} from './structureKit';
import type { DocCommands } from './types';

const VAR_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;
/** Variable types (checked at run time too: arguments may come from the Raw panel). */
const VAR_TYPES: ReadonlySet<string> = new Set(['float', 'vec2', 'color']);

/** Phira's built-in shaders (the record keeps this list complete against the model's `BuiltinShader`). */
const BUILTIN_SHADER_SET: Readonly<Record<BuiltinShader, true>> = {
  chromatic: true,
  circleBlur: true,
  fisheye: true,
  glitch: true,
  grayscale: true,
  noise: true,
  pixel: true,
  radialBlur: true,
  shockwave: true,
  vignette: true,
};
const BUILTIN_SHADERS: ReadonlySet<string> = new Set(Object.keys(BUILTIN_SHADER_SET));

/** How a video fills the screen (extra.json `scale`): cover, letterbox, stretch. */
const VIDEO_SCALES: ReadonlySet<string> = new Set<Video['scale']>(['cropCenter', 'inside', 'fit']);

/** True for the name of one of Phira's built-in shaders (checked at run time: names may come from raw data). */
export function isBuiltinShader(name: string): name is BuiltinShader {
  return BUILTIN_SHADERS.has(name);
}

/** Deletes an effect with its variable tracks and its place in the effect order. */
export function deleteEffectRecord(tx: Tx, id: EffectId): void {
  const e = tx.doc.effects.get(id);
  if (e === undefined) return;
  for (const v of e.vars) if (v.value.kind === 'track') deleteTrack(tx, v.value.track);
  tx.remove('effects', id);
  tx.setOrder(
    'effectOrder',
    tx.doc.effectOrder.filter((x) => x !== id),
  );
}

/** Deletes a video with its variable tracks. */
export function deleteVideoRecord(tx: Tx, id: VideoId): void {
  const v = tx.doc.videos.get(id);
  if (v === undefined) return;
  for (const which of ['alpha', 'dim'] as const) {
    const val = v[which];
    if (val.kind === 'track') deleteTrack(tx, val.track);
  }
  tx.remove('videos', id);
}

function checkRange(tx: Tx, start: Beat, end: Beat): void {
  if (!Beats.isNormal(start) || !Beats.isNormal(end)) tx.fail({ key: 'cmd.badBeat' });
  if (!Beats.lt(start, end)) tx.fail({ key: 'cmd.effects.range' });
}

/** True for custom shader files (GLSL ES 1.00 fragment shaders). */
export function isShaderAsset(a: AssetRef): boolean {
  return a.role === 'shader' || a.mime.startsWith('text/') || /\.(glsl|frag|fs)$/i.test(a.path);
}

const addEffect: DocCommands['addEffect'] = (tx: Tx, { start, end, shader, global }) => {
  checkRange(tx, start, end);
  if (shader.kind === 'asset') {
    const a = assetOf(tx, shader.asset);
    if (!isShaderAsset(a)) tx.fail({ key: 'cmd.effects.notShader', params: { path: a.path } });
  } else if (!isBuiltinShader(shader.name)) {
    tx.fail({ key: 'cmd.effects.unknownShader', params: { name: String(shader.name) } });
  }
  const id: EffectId = tx.newId('effects');
  const rec: Effect = {
    id,
    start,
    end,
    shader: shader.kind === 'builtin' ? { kind: 'builtin', name: shader.name } : { kind: 'asset', asset: shader.asset },
    global: global ?? false,
    vars: [],
  };
  tx.insert('effects', rec);
  tx.setOrder('effectOrder', [...tx.doc.effectOrder, id]);
  tx.setSelection({ effects: [id] });
  tx.setFocus({ beat: start });
  return id;
};

const patchEffect: DocCommands['patchEffect'] = (tx: Tx, { id, start, end, global }) => {
  const e = effectOf(tx, id);
  const ns = start ?? e.start;
  const ne = end ?? e.end;
  checkRange(tx, ns, ne);
  const patch: Patch<Effect> = {};
  if (!Beats.eq(ns, e.start)) patch.start = ns;
  if (!Beats.eq(ne, e.end)) patch.end = ne;
  if (global !== undefined && global !== e.global) patch.global = global;
  if (Object.keys(patch).length > 0) tx.update('effects', id, patch);
};

const setEffectVar: DocCommands['setEffectVar'] = (tx: Tx, { id, v }) => {
  const e = effectOf(tx, id);
  if (!VAR_NAME_RE.test(v.name)) tx.fail({ key: 'cmd.effects.varName', params: { name: v.name } });
  if (!VAR_TYPES.has(v.type)) tx.fail({ key: 'cmd.effects.varValue', params: { name: v.name } });
  const val = v.value;
  if (val.kind === 'const' && !isTrackValueOf(varValueType(v.type), val.v)) {
    tx.fail({ key: 'cmd.effects.varValue', params: { name: v.name } });
  }
  if (val.kind === 'track') {
    const t = tx.doc.tracks.get(val.track);
    const owned = t?.owner.kind === 'effectVar' && t.owner.effect === id && t.owner.name === v.name;
    if (!owned || t.valueType !== varValueType(v.type))
      tx.fail({ key: 'cmd.effects.varTrack', params: { name: v.name } });
  }
  const old = e.vars.find((x) => x.name === v.name);
  if (old?.value.kind === 'track' && !(val.kind === 'track' && val.track === old.value.track)) {
    deleteTrack(tx, old.value.track);
  }
  const next: EffectVar = { name: v.name, type: v.type, value: val };
  const vars = old === undefined ? [...e.vars, next] : e.vars.map((x) => (x.name === v.name ? next : x));
  tx.update('effects', id, { vars });
};

const deleteEffect: DocCommands['deleteEffect'] = (tx: Tx, { id }) => {
  effectOf(tx, id);
  deleteEffectRecord(tx, id);
  tx.setSelection({ effects: [] });
};

const reorderEffects: DocCommands['reorderEffects'] = (tx: Tx, { order }) => {
  const next = reorderedSubset(tx.doc.effectOrder, order);
  if (next === null) tx.fail({ key: 'cmd.effects.orderInvalid' });
  tx.setOrder('effectOrder', next);
};

/** True for video files. */
export function isVideoAsset(a: AssetRef): boolean {
  return a.role === 'video' || a.mime.startsWith('video/');
}

const addVideo: DocCommands['addVideo'] = (tx: Tx, { asset, time }) => {
  const a = assetOf(tx, asset);
  if (!isVideoAsset(a)) tx.fail({ key: 'cmd.videos.notVideo', params: { path: a.path } });
  if (!Beats.isNormal(time)) tx.fail({ key: 'cmd.badBeat' });
  const id: VideoId = tx.newId('videos');
  const rec: Video = {
    id,
    asset,
    time,
    scale: 'cropCenter',
    alpha: { kind: 'default' },
    dim: { kind: 'default' },
    attachLine: null,
  };
  tx.insert('videos', rec);
  tx.setSelection({ videos: [id] });
  tx.setFocus({ beat: time });
  return id;
};

function checkVideoVar(tx: Tx, id: VideoId, which: 'alpha' | 'dim', val: VideoVarValue): void {
  if (val.kind === 'const' && !isFiniteNumber(val.v)) tx.fail({ key: 'cmd.videos.varValue', params: { which } });
  if (val.kind === 'track') {
    const t = tx.doc.tracks.get(val.track);
    const owned = t?.owner.kind === 'videoVar' && t.owner.video === id && t.owner.which === which;
    if (!owned || t.valueType !== 'number') tx.fail({ key: 'cmd.videos.varTrack', params: { which } });
  }
}

const patchVideo: DocCommands['patchVideo'] = (tx: Tx, { id, patch }) => {
  const v = videoOf(tx, id);
  const clean: Patch<Video> = {};
  if (patch.time !== undefined) {
    if (!Beats.isNormal(patch.time)) tx.fail({ key: 'cmd.badBeat' });
    clean.time = patch.time;
  }
  if (patch.scale !== undefined) {
    if (!VIDEO_SCALES.has(patch.scale)) tx.fail({ key: 'cmd.videos.scale' });
    clean.scale = patch.scale;
  }
  if (patch.attachLine !== undefined) {
    if (patch.attachLine !== null) lineOf(tx, patch.attachLine);
    clean.attachLine = patch.attachLine;
  }
  for (const which of ['alpha', 'dim'] as const) {
    const val = patch[which];
    if (val === undefined) continue;
    checkVideoVar(tx, id, which, val);
    const old = v[which];
    if (old.kind === 'track' && !(val.kind === 'track' && val.track === old.track)) deleteTrack(tx, old.track);
    clean[which] = val;
  }
  if (Object.keys(clean).length > 0) tx.update('videos', id, clean);
};

const deleteVideo: DocCommands['deleteVideo'] = (tx: Tx, { id }) => {
  videoOf(tx, id);
  deleteVideoRecord(tx, id);
  tx.setSelection({ videos: [] });
};

export const effectCommands = {
  addEffect,
  patchEffect,
  setEffectVar,
  deleteEffect,
  reorderEffects,
  addVideo,
  patchVideo,
  deleteVideo,
} as const;
